import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { EmptyStateBox } from "../EmptyStateBox/EmptyStateBox";
import { Paragraph, Span } from "../Typography/Typography";
import {
    searchAlbums,
    ensureAlbum,
    albumPath,
    SearchAlbumResult,
} from "../../api/albums";

import "./AlbumSearch.scss";

function SearchResultItem({
    album,
    searchQuery,
    index,
}: {
    album: SearchAlbumResult;
    searchQuery: string;
    index: number;
}) {
    const navigate = useNavigate();
    const [isOpening, setIsOpening] = React.useState(false);
    const [openError, setOpenError] = React.useState<string | null>(null);
    const itemStyle = { "--result-index": index } as React.CSSProperties;

    const openAlbum = (albumId: number) => {
        navigate(albumPath(albumId, searchQuery));
    };

    const handleOpen = async () => {
        if (album.id) {
            openAlbum(album.id);
            return;
        }

        setIsOpening(true);
        setOpenError(null);

        try {
            const saved = await ensureAlbum(album);
            openAlbum(saved.id);
        } catch (error) {
            console.error("Failed to open album:", error);
            setOpenError("Could not open this album.");
        } finally {
            setIsOpening(false);
        }
    };

    const content = (
        <>
            {album.source_cover_url ? (
                <img className="album-search__cover" src={album.source_cover_url} alt="" />
            ) : (
                <div className="album-search__cover album-search__cover--placeholder" />
            )}
            <div className="album-search__meta">
                <Span variant="body-2" weight="bold" color="high-contrast">
                    {album.title}
                </Span>
                <Span variant="body-3" color="mid-contrast">
                    {album.artist_name}
                </Span>
                {album.in_library && (
                    <Span className="album-search__badge" variant="body-4" color="mid-contrast">
                        In library
                    </Span>
                )}
                {isOpening && (
                    <Span variant="body-4" color="mid-contrast">
                        Opening…
                    </Span>
                )}
                {openError && (
                    <Span variant="body-4" color="mid-contrast">
                        {openError}
                    </Span>
                )}
            </div>
        </>
    );

    if (album.id) {
        return (
            <li className="album-search__result" style={itemStyle}>
                <Link className="album-search__result-link" to={albumPath(album.id, searchQuery)}>
                    {content}
                </Link>
            </li>
        );
    }

    return (
        <li className="album-search__result" style={itemStyle}>
            <button
                type="button"
                className="album-search__result-link album-search__result-button"
                onClick={handleOpen}
                disabled={isOpening}
            >
                {content}
            </button>
        </li>
    );
}

export function AlbumSearch() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [query, setQuery] = React.useState(() => searchParams.get("q") ?? "");
    const [results, setResults] = React.useState<SearchAlbumResult[]>([]);
    const [resultsGeneration, setResultsGeneration] = React.useState(0);
    const [configWarning, setConfigWarning] = React.useState<string | null>(null);
    const [isSearching, setIsSearching] = React.useState(false);
    const [searchError, setSearchError] = React.useState<string | null>(null);
    const searchRequestRef = React.useRef(0);

    React.useEffect(() => {
        const trimmed = query.trim();
        const timeout = setTimeout(() => {
            if (trimmed) {
                setSearchParams({ q: trimmed }, { replace: true });
            } else {
                setSearchParams({}, { replace: true });
            }
        }, 400);

        return () => clearTimeout(timeout);
    }, [query, setSearchParams]);

    React.useEffect(() => {
        const trimmed = query.trim();

        if (trimmed.length < 2) {
            searchRequestRef.current += 1;
            setResults([]);
            setSearchError(null);
            setConfigWarning(null);
            setIsSearching(false);
            return;
        }

        const requestId = ++searchRequestRef.current;
        setIsSearching(true);
        setSearchError(null);
        setConfigWarning(null);

        const timeout = setTimeout(async () => {
            try {
                const data = await searchAlbums(trimmed);
                if (requestId !== searchRequestRef.current) {
                    return;
                }

                setResults(data.results);
                setResultsGeneration((generation) => generation + 1);

                if (!data.meta.spotify_configured) {
                    console.warn(
                        "Spotify is not configured on the API server. Add SPOTIFY_CLIENT_ID and SPOTIFY_CLIENT_SECRET to backend/.env, then restart vercel dev."
                    );
                } else if (data.meta.spotify_error) {
                    setConfigWarning(`Spotify search failed: ${data.meta.spotify_error}`);
                }
            } catch (error) {
                if (requestId !== searchRequestRef.current) {
                    return;
                }

                console.error("Album search failed:", error);
                setSearchError("Search failed. Please try again.");
                setResults([]);
            } finally {
                if (requestId === searchRequestRef.current) {
                    setIsSearching(false);
                }
            }
        }, 450);

        return () => clearTimeout(timeout);
    }, [query]);

    return (
        <div className="album-search">
            <label className="album-search__label" htmlFor="album-search-input">
                Search albums
            </label>
            <input
                autoFocus
                id="album-search-input"
                className="album-search__input"
                type="search"
                placeholder="Album or artist name…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                autoComplete="off"
            />

            {isSearching && (
                <div className="album-search__status">
                    <LoadingSpinner />
                </div>
            )}

            {searchError && (
                <Paragraph className="album-search__error" variant="body-2" color="mid-contrast">
                    {searchError}
                </Paragraph>
            )}

            {configWarning && (
                <Paragraph className="album-search__error" variant="body-2" color="mid-contrast">
                    {configWarning}
                </Paragraph>
            )}

            {!isSearching && query.trim().length >= 2 && results.length === 0 && !searchError && !configWarning && (
                <EmptyStateBox>
                    No albums found for &ldquo;{query.trim()}&rdquo;.
                </EmptyStateBox>
            )}

            {!isSearching && results.length > 0 && (
                <ul className="album-search__results" key={resultsGeneration}>
                    {results.map((album, index) => (
                        <SearchResultItem
                            key={album.source_id}
                            album={album}
                            searchQuery={query}
                            index={index}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
}
